import React, { useState } from "react";
import { ChevronDown, ChevronUp, Filter, Heart, Info, Minus, Users } from "lucide-react";
import { CUSTOM_EVENT_TYPES, useCustomAudiences, useLocaleSearch, useLocationSearch, useTargetingSearch } from "./api";
import {
  AdSet,
  AGES,
  ATTRIBUTION_OPTIONS,
  CONVERSION_LOCATIONS,
  FALLBACK_BEHAVIOURS,
  FALLBACK_CUSTOM_AUDIENCES,
  FALLBACK_INTERESTS,
  FALLBACK_LOCALES,
  FALLBACK_LOCATIONS,
  OPTIMIZATION_OPTIONS,
  PLACEMENT_OPTIONS,
  SEGMENT_OPTIONS,
} from "./types";
import {
  AddButton,
  AsyncChipPicker,
  AsyncSingleSelect,
  Field,
  InfoChip,
  ListPicker,
  PickItem,
  RadioCard,
  SectionHeader,
  SelectBox,
  TagPicker,
  ghostBtn,
  inputCls,
  primaryBtn,
} from "./ui";

type Section = "audience" | "placements" | "optimization";
type SuggestionTab = "segments" | "custom" | "interests";

const SUGGESTION_TABS: { id: SuggestionTab; label: string; icon: React.ReactNode }[] = [
  { id: "segments", label: "Saved Segments", icon: <Users size={20} /> },
  { id: "custom", label: "Custom Audience", icon: <Filter size={20} /> },
  { id: "interests", label: "Interests and Behaviours", icon: <Heart size={20} /> },
];

// Collapsed, an accordion can show a one-line summary of what's set inside.
const Accordion: React.FC<{
  title: string;
  subtitle: string;
  summary?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, summary, open, onToggle, onClose, children }) => (
  <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm">
    <button type="button" onClick={onToggle} className="w-full flex items-start justify-between gap-4 px-4 py-4 text-left">
      <span className="min-w-0">
        <span className="block text-base text-gray-900 dark:text-gray-100">{title}</span>
        <span className="block text-sm text-gray-600 dark:text-gray-400">{subtitle}</span>
        {!open && summary && <span className="mt-3 flex flex-wrap gap-2">{summary}</span>}
      </span>
      {open ? <ChevronUp size={20} className="mt-1 shrink-0" /> : <ChevronDown size={20} className="mt-1 shrink-0" />}
    </button>
    {open && (
      <div className="px-4 pb-4">
        {children}
        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" className={ghostBtn} onClick={onClose}>
            Back
          </button>
          <button type="button" className={primaryBtn} onClick={onClose}>
            Save
          </button>
        </div>
      </div>
    )}
  </div>
);

const SubCard: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-4">
    <p className="text-base text-gray-900 dark:text-gray-100">{title}</p>
    <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
    <div className="mt-3">{children}</div>
  </div>
);

const matchesQuery = (name: string, query: string) => name.toLowerCase().includes(query.trim().toLowerCase());

const ChipGroup: React.FC<{ label: string; items: string[] }> = ({ label, items }) => (
  <span className="inline-flex flex-wrap items-center gap-1.5 text-sm text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1">
    {label}:
    {items.map((c) => (
      <InfoChip key={c} value={c} />
    ))}
  </span>
);

// Audience is the one section wired to live Meta data: locations, interests,
// behaviors and languages all come from the search endpoints (api.ts),
// scoped by the sender chosen in Broadcast Details. Custom Audiences is a
// plain list fetched once per sender rather than a search. Saved Segments
// stays local-only — the docs say segments aren't synced to Meta yet.
const AudienceSection: React.FC<{ adSet: AdSet; update: (patch: Partial<AdSet>) => void; senderId: string }> = ({ adSet, update, senderId }) => {
  const [tab, setTab] = useState<SuggestionTab>("segments");
  const ages = AGES.map(String);

  const locationSearch = useLocationSearch(senderId);
  const interestSearch = useTargetingSearch("interests", senderId);
  const behaviorSearch = useTargetingSearch("behaviors", senderId);
  const localeSearch = useLocaleSearch(senderId);
  const customAudiences = useCustomAudiences(senderId);

  // Location/Interests/Behaviours/Language/Custom-Audiences all fall back to
  // a small hardcoded, client-filtered list while there's no senderId to
  // scope the real Meta search by (see FALLBACK_* in types.ts) — the real,
  // sender-scoped search takes over the moment a sender is chosen. (Location
  // was briefly forced to always use the fallback list regardless of sender —
  // reverted now that sender profiles are coming through reliably; the real
  // bug was useRequest sending a literal `q=undefined` on the first search,
  // fixed in use-req.tsx.)
  const locationOptions: PickItem[] = senderId
    ? locationSearch.items.map((l) => ({ key: `${l.type}:${l.key}`, label: l.type === "city" ? `${l.name}, ${l.country_name || ""}`.replace(/, $/, "") : l.name }))
    : FALLBACK_LOCATIONS.filter((l) => matchesQuery(l.name, locationSearch.query)).map((l) => ({ key: `${l.type}:${l.key}`, label: l.name }));
  const locationValue: PickItem[] = adSet.locations.map((l) => ({ key: `${l.type}:${l.key}`, label: l.name }));

  return (
    <div className="space-y-5">
      <div>
        <p className="text-base text-gray-800 dark:text-gray-200 mb-2">Audience Type</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RadioCard selected={adSet.audienceType === "advantage"} onClick={() => update({ audienceType: "advantage" })} title="Advantage+ Audience" badge="Recommended">
            <span className="block">Let Meta find people most likely to respond to your ad.</span>
            <span className="block mt-1">You provide a few controls and suggestions and Meta may expand your audience to improve performance.</span>
          </RadioCard>
          <RadioCard selected={adSet.audienceType === "manual"} onClick={() => update({ audienceType: "manual" })} title="Manual Audience">
            <span className="block">Define a specific audience using detailed targeting options.</span>
            <span className="block">Your ads will be shown to people who match your selection criteria.</span>
          </RadioCard>
        </div>
      </div>

      <SubCard title="Location" subtitle="Choose where you want the ads to be shown">
        {!senderId && <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">Showing example locations. Pick a Sender Profile above to search real ones.</p>}
        <AsyncChipPicker
          label="Location"
          placeholder="Search countries, regions or cities"
          query={locationSearch.query}
          onQueryChange={locationSearch.setQuery}
          options={locationOptions}
          loading={senderId ? locationSearch.loading : false}
          value={locationValue}
          minChars={0}
          onChange={(next) =>
            update({
              locations: next.map((n) => {
                const [type, key] = n.key.split(/:(.+)/);
                return { key, type: type as "country" | "region" | "city", name: n.label };
              }),
            })
          }
        />
      </SubCard>

      <SubCard title="Demographics" subtitle="Set the basic demographic details about your audience">
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1.3fr_1fr] gap-5">
          <Field label="Age">
            <div className="grid grid-cols-2 gap-2">
              <SelectBox value={String(adSet.ageFrom)} onChange={(v) => update({ ageFrom: Number(v) })} options={ages} prefix="From" />
              <SelectBox value={String(adSet.ageTo)} onChange={(v) => update({ ageTo: Number(v) })} options={ages} prefix="To" />
            </div>
          </Field>
          <Field label="Gender">
            <div className="grid grid-cols-3 gap-2">
              {(["all", "men", "women"] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => update({ gender: g })}
                  className="flex items-center gap-2 px-3 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm capitalize"
                >
                  {/* Literal blue, not `primary-700` — undefined repo-wide (see ui.tsx's RadioCard/primaryBtn fixes on this page). */}
                  <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${adSet.gender === g ? "border-blue-600" : "border-gray-300"}`}>
                    {adSet.gender === g && <span className="w-2 h-2 rounded-full bg-blue-600" />}
                  </span>
                  <span className={adSet.gender === g ? "text-gray-900 dark:text-gray-100" : "text-gray-500"}>{g}</span>
                </button>
              ))}
            </div>
          </Field>
          <div>
            <label className="flex items-center gap-1.5 text-sm text-gray-800 dark:text-gray-200">
              Languages <Info size={15} className="text-primary-700" aria-label="Language targeting" />
            </label>
            <div className="mt-2">
              <AsyncSingleSelect
                query={localeSearch.query}
                onQueryChange={localeSearch.setQuery}
                options={
                  senderId
                    ? localeSearch.items.map((l) => ({ key: String(l.key), label: l.name }))
                    : FALLBACK_LOCALES.filter((l) => matchesQuery(l.name, localeSearch.query)).map((l) => ({ key: String(l.key), label: l.name }))
                }
                loading={senderId ? localeSearch.loading : false}
                value={adSet.language ? { key: String(adSet.language.key), label: adSet.language.name } : null}
                onChange={(item) => update({ language: { key: Number(item.key), name: item.label } })}
                placeholder="Search languages"
                minChars={senderId ? 2 : 0}
              />
            </div>
          </div>
        </div>
      </SubCard>

      <div>
        <p className="text-base text-gray-900 dark:text-gray-100">Audience Suggestions</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">Add signals to help Meta understand who you are most interested in reaching.</p>
        <div className="mt-3 grid grid-cols-3 gap-1 p-1.5 rounded-lg bg-primary-50 dark:bg-primary-500/10">
          {SUGGESTION_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center justify-center gap-2 py-3 rounded-md text-base transition-colors ${
                tab === t.id ? "bg-white dark:bg-gray-900 text-primary-700 shadow-sm" : "text-gray-800 dark:text-gray-200"
              }`}
            >
              {t.icon} <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-3">
          {tab === "segments" && (
            <TagPicker label="Saved Segments" placeholder="Search Segments by name or tag. . ." suggestions={SEGMENT_OPTIONS} value={adSet.savedSegments} onChange={(v) => update({ savedSegments: v })} />
          )}
          {tab === "custom" && (
            <>
              {!senderId && <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">Showing example custom audiences. Pick a Sender Profile above for your real ones.</p>}
              <ListPicker
                placeholder="Search Custom Audiences. . ."
                options={(senderId ? customAudiences.items : FALLBACK_CUSTOM_AUDIENCES).map((c) => ({ key: c.id, label: c.name }))}
                value={adSet.customAudiences.map((c) => c.id)}
                loading={senderId ? customAudiences.loading : false}
                emptyLabel="No custom audiences found."
                onChange={(keys) =>
                  update({
                    customAudiences: (senderId ? customAudiences.items : FALLBACK_CUSTOM_AUDIENCES).filter((c) => keys.includes(c.id)).map((c) => ({ id: c.id, name: c.name })),
                  })
                }
              />
            </>
          )}
          {tab === "interests" && (
            <div>
              {!senderId && <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">Showing example interests and behaviours. Pick a Sender Profile above to search real ones.</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <AsyncChipPicker
                  label="Interests"
                  placeholder="Search Interests"
                  query={interestSearch.query}
                  onQueryChange={interestSearch.setQuery}
                  options={
                    senderId
                      ? interestSearch.items.map((i) => ({ key: i.id, label: i.name }))
                      : FALLBACK_INTERESTS.filter((i) => matchesQuery(i.name, interestSearch.query)).map((i) => ({ key: i.id, label: i.name }))
                  }
                  loading={senderId ? interestSearch.loading : false}
                  value={adSet.interests.map((i) => ({ key: i.id, label: i.name }))}
                  minChars={senderId ? 2 : 0}
                  onChange={(next) => update({ interests: next.map((n) => ({ id: n.key, name: n.label })) })}
                />
                <AsyncChipPicker
                  label="Behaviours"
                  placeholder="Search Behaviours"
                  query={behaviorSearch.query}
                  onQueryChange={behaviorSearch.setQuery}
                  options={
                    senderId
                      ? behaviorSearch.items.map((i) => ({ key: i.id, label: i.name }))
                      : FALLBACK_BEHAVIOURS.filter((i) => matchesQuery(i.name, behaviorSearch.query)).map((i) => ({ key: i.id, label: i.name }))
                  }
                  loading={senderId ? behaviorSearch.loading : false}
                  value={adSet.behaviours.map((i) => ({ key: i.id, label: i.name }))}
                  minChars={senderId ? 2 : 0}
                  onChange={(next) => update({ behaviours: next.map((n) => ({ id: n.key, name: n.label })) })}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlacementsSection: React.FC<{ adSet: AdSet; update: (patch: Partial<AdSet>) => void }> = ({ adSet, update }) => {
  const optimised = adSet.placementMode === "optimised";
  const toggle = (p: string) =>
    update({ manualPlacements: adSet.manualPlacements.includes(p) ? adSet.manualPlacements.filter((x) => x !== p) : [...adSet.manualPlacements, p] });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RadioCard selected={optimised} onClick={() => update({ placementMode: "optimised" })} title="Optimised Placements">
          Let ConvoHub optimise delivery across eligible Instagram placements.
        </RadioCard>
        <RadioCard selected={!optimised} onClick={() => update({ placementMode: "manual" })} title="Manual placements">
          Choose exactly where this ad can appear.
        </RadioCard>
      </div>
      <div>
        <p className="text-base text-gray-800 dark:text-gray-200">Included Placements</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {optimised ? "Placements are automatically optimised by Meta" : "Select the placements this ad can run on"}
        </p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {PLACEMENT_OPTIONS.map((p) => {
            const checked = adSet.manualPlacements.includes(p);
            return (
              <label
                key={p}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-base ${
                  optimised ? "text-gray-400 cursor-not-allowed" : "text-gray-800 dark:text-gray-100 cursor-pointer"
                }`}
              >
                {optimised ? (
                  <span className="w-5 h-5 rounded border border-gray-300 bg-gray-100 flex items-center justify-center">
                    <Minus size={14} className="text-gray-400" />
                  </span>
                ) : (
                  <input type="checkbox" checked={checked} onChange={() => toggle(p)} className="w-5 h-5 rounded border-gray-300 text-primary-700 focus:ring-primary-500" />
                )}
                {p}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LabeledSelect: React.FC<{ label: string; hint: string; value: string; options: string[]; onChange: (v: string) => void }> = ({
  label,
  hint,
  value,
  options,
  onChange,
}) => (
  <Field label={label} hint={hint}>
    <SelectBox value={value} onChange={onChange} options={options} />
  </Field>
);

const OptimizationSection: React.FC<{ adSet: AdSet; update: (patch: Partial<AdSet>) => void; goal: string }> = ({ adSet, update, goal }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <LabeledSelect label="Optimization for" hint="Select what you want Meta to optimize for" value={adSet.optimization} options={OPTIMIZATION_OPTIONS} onChange={(v) => update({ optimization: v })} />
      <LabeledSelect label="Conversion Location" hint="Where do you want conversions?" value={adSet.conversionLocation} options={CONVERSION_LOCATIONS} onChange={(v) => update({ conversionLocation: v })} />
      <LabeledSelect label="Attribution Setting" hint="Select how conversions are attributed to your ads" value={adSet.attribution} options={ATTRIBUTION_OPTIONS} onChange={(v) => update({ attribution: v })} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Destination Domain" hint="Informational only — not sent with the ad yet">
        <input value={adSet.destinationDomain} onChange={(e) => update({ destinationDomain: e.target.value })} className={inputCls} />
      </Field>
      <Field label="UTM Tracking" hint="Appended to the ad's Destination URL on launch">
        <input value={adSet.utm} onChange={(e) => update({ utm: e.target.value })} className={inputCls} />
      </Field>
    </div>
    {(goal === "Sales" || goal === "Leads") && (
      <div>
        <p className="text-base text-gray-800 dark:text-gray-200">Promoted Object</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Meta requires this for a {goal} broadcast — the pixel that tracks the event you're optimizing for.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Meta Pixel ID" hint="From Meta Events Manager, for the ad account running this broadcast">
            <input
              value={adSet.promotedObjectPixelId}
              onChange={(e) => update({ promotedObjectPixelId: e.target.value })}
              placeholder="e.g. 123456789012345"
              className={inputCls}
            />
          </Field>
          <LabeledSelect
            label="Custom Event Type"
            hint="Which pixel event counts as a conversion"
            value={CUSTOM_EVENT_TYPES.find((c) => c.value === adSet.promotedObjectEventType)?.label || CUSTOM_EVENT_TYPES[0].label}
            options={CUSTOM_EVENT_TYPES.map((c) => c.label)}
            onChange={(label) => {
              const hit = CUSTOM_EVENT_TYPES.find((c) => c.label === label);
              if (hit) update({ promotedObjectEventType: hit.value });
            }}
          />
        </div>
        {!adSet.promotedObjectPixelId.trim() && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">Required to continue — Meta rejects a {goal} broadcast without it.</p>
        )}
      </div>
    )}
  </div>
);

const AdSetDetails: React.FC<{
  adSets: AdSet[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  updateAdSet: (patch: Partial<AdSet>) => void;
  addAdSet: () => void;
  senderId: string;
  goal: string;
}> = ({ adSets, activeIndex, setActiveIndex, updateAdSet, addAdSet, senderId, goal }) => {
  const [open, setOpen] = useState<Section | null>(null);
  const adSet = adSets[activeIndex];
  const toggle = (s: Section) => setOpen((cur) => (cur === s ? null : s));

  return (
    <div>
      <SectionHeader
        title="Ad Set Details"
        subtitle="We have pre-filled this based on your brief. You may edit anything or proceed."
        action={<AddButton label="Create New Ad Set" onClick={addAdSet} />}
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {adSets.map((a, i) => (
          <button
            key={a.id}
            type="button"
            onClick={() => setActiveIndex(i)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg bg-white dark:bg-gray-900 text-base ${
              i === activeIndex ? "border border-primary-700" : "border border-gray-300 dark:border-gray-700"
            }`}
          >
            <span className="text-gray-900 dark:text-gray-100">{a.name || "New Ad Set"}</span>
            <span className="text-sm text-gray-700 dark:text-gray-300 bg-primary-50 dark:bg-primary-500/15 rounded px-1.5 py-0.5">Ad set {i + 1}</span>
          </button>
        ))}
      </div>

      <Field label="Ad Set Name" hint="Give your ad set a name">
        <input value={adSet.name} onChange={(e) => updateAdSet({ name: e.target.value })} placeholder="Enter a name for this ad set" className={inputCls} />
      </Field>

      <div className="mt-8 space-y-3">
        <Accordion
          title="Audience"
          subtitle="These set the boundaries for where Meta can deliver your ads"
          summary={
            <>
              <ChipGroup label="Locations" items={adSet.locations.map((l) => l.name)} />
              {adSet.interests.length > 0 && <ChipGroup label="Interests" items={adSet.interests.map((i) => i.name)} />}
            </>
          }
          open={open === "audience"}
          onToggle={() => toggle("audience")}
          onClose={() => setOpen(null)}
        >
          <AudienceSection adSet={adSet} update={updateAdSet} senderId={senderId} />
        </Accordion>
        <Accordion
          title="Ad Placements"
          subtitle="Choose automatic placement or select placements manually."
          summary={<InfoChip value={adSet.placementMode === "optimised" ? "Optimised Placements" : `Manual Placements (${adSet.manualPlacements.length})`} />}
          open={open === "placements"}
          onToggle={() => toggle("placements")}
          onClose={() => setOpen(null)}
        >
          <PlacementsSection adSet={adSet} update={updateAdSet} />
        </Accordion>
        <Accordion
          title="Optimization"
          subtitle="Choose how you want to optimize your ad set"
          open={open === "optimization"}
          onToggle={() => toggle("optimization")}
          onClose={() => setOpen(null)}
        >
          <OptimizationSection adSet={adSet} update={updateAdSet} goal={goal} />
        </Accordion>
      </div>
    </div>
  );
};

export default AdSetDetails;
